import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Input,
	OnChanges,
	Optional,
	SimpleChanges
} from '@angular/core';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import type { ActivityModel } from 'go-modules/models/activity/activity.schema';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MarkerSetPanelComponent } from '@go-modules/components/marker-set-panel/marker-set-panel.component';
import { ActivityFormService } from '../activity-form/activity-form.service';
import { CONTENT_TYPES, MODES } from '@go-modules/components/library/library-collections-viewer/library-collection-viewer.constants';
import type { MarkerSet } from '@go-modules/interfaces/markers/marker';
import { NgxTagSetService } from '@go-modules/services/tag-set/ngx-tag-set.service';
import { GoModalService } from '@go-modules/services/go-modal/go-modal.service';
import { LibraryDialogComponent, LibraryDialogData } from '@go-modules/components/dialogs/library-dialog/library-dialog.component';
import { firstValueFrom } from 'rxjs';
import { isDismissedModalResult } from '@go-modules/utilities';
import { ActivityEditAccessibilityAnnouncerService } from '../activity-edit-accessibility-announcer.service';

interface TagSetResource extends MarkerSet {
	$promise?: Promise<MarkerSet>;
}

@Component({
	selector: 'ngx-activity-edit-marker-section',
	standalone: true,
	imports: [
		CommonModule,
		TranslateModule,
		MarkerSetPanelComponent
	],
	templateUrl: './ngx-activity-edit-marker-section.component.html',
	styleUrls: ['./ngx-activity-edit-marker-section.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class NgxActivityEditMarkerSectionComponent implements OnChanges {
	@Input() public activity: ActivityModel | null = null;

	public selectedMarkerSet: MarkerSet | TagSetResource | null = null;
	private initialTagSetId: number | null = null;
	private initialMarkerSet: MarkerSet | null = null;

	constructor (
		private activityFormService: ActivityFormService,
		private ngxGoModalService: GoModalService,
		private tagSetService: NgxTagSetService,
		private liveAnnouncer: LiveAnnouncer,
		private translate: TranslateService,
		private cdr: ChangeDetectorRef,
		@Optional() private activityEditAccessibilityAnnouncer?: ActivityEditAccessibilityAnnouncerService
	) {}

	public ngOnChanges (changes: SimpleChanges): void {
		if (changes.activity?.currentValue) {
			this.initializeMarkerSelection();
		}
	}

	public get hasMarkerSet (): boolean {
		return !!this.selectedMarkerSet;
	}

	public shouldShowMarkerSection (): boolean {
		return !!this.activity?.isViewable?.('tag_set_id');
	}

	public openMarkerSelectionModal (): void {
		const hadMarkerSet = this.hasMarkerSet;
		const data: LibraryDialogData = {
			filterType: [CONTENT_TYPES.MARKERS],
			mode: MODES.SELECT,
			collectionManagement: true
		};
		const dialogRef = this.ngxGoModalService.open(LibraryDialogComponent, false, { data });

		dialogRef.afterClosed().subscribe((markers: MarkerSet | null | undefined) => {
			if (!markers || isDismissedModalResult(markers)) {
				return;
			}
			this.selectedMarkerSet = markers as TagSetResource;
			this.sortMarkerSetTags();
			this.setMarkerControlValue(this.selectedMarkerSet?.id ?? null, true);
			this.announceMarkerSetChange(hadMarkerSet);
			this.cdr.markForCheck();
		});
	}

	public handleMarkerSetEdited ({ markerSet }: { markerSet: MarkerSet }): void {
		const hadMarkerSet = this.hasMarkerSet;
		this.selectedMarkerSet = markerSet as TagSetResource;
		this.sortMarkerSetTags();
		this.setMarkerControlValue(this.selectedMarkerSet?.id ?? null, true);
		this.announceMarkerSetChange(hadMarkerSet);
		this.cdr.markForCheck();
	}

	public handleMarkerSetRemoved (): void {
		const hadMarkerSet = this.hasMarkerSet;
		this.selectedMarkerSet = null;
		this.setMarkerControlValue(null, true);
		this.announceMarkerSetChange(hadMarkerSet);
		this.cdr.markForCheck();
	}

	public resetSelection (): void {
		if (this.initialMarkerSet) {
			this.selectedMarkerSet = this.cloneMarkerSet(this.initialMarkerSet);
			this.sortMarkerSetTags();
			this.cdr.markForCheck();
		} else if (this.initialTagSetId !== null) {
			this.loadMarkerSet(this.initialTagSetId, true).catch(() => {});
		} else {
			this.selectedMarkerSet = null;
			this.cdr.markForCheck();
		}
		this.setMarkerControlValue(this.initialTagSetId, false);
	}

	public clearSelection (): void {
		this.selectedMarkerSet = null;
		this.setMarkerControlValue(null, true);
		this.cdr.markForCheck();
	}

	private initializeMarkerSelection (): void {
		if (!this.activity) {
			return;
		}
		const tagSetId = this.activity.tag_set_id ?? null;
		this.initialTagSetId = tagSetId;
		this.initialMarkerSet = null;
		this.setMarkerControlValue(tagSetId, false);

		if (tagSetId !== null) {
			this.loadMarkerSet(tagSetId, true).catch(() => {});
		} else {
			this.selectedMarkerSet = null;
			this.initialMarkerSet = null;
			this.cdr.markForCheck();
		}
	}

	private async loadMarkerSet (markerSetId: number | null, cacheAsInitial: boolean = false): Promise<void> {
		if (markerSetId === null) {
			this.selectedMarkerSet = null;
			if (cacheAsInitial) {
				this.initialMarkerSet = null;
			}
			this.cdr.markForCheck();
			return;
		}
		try {
			const result = await firstValueFrom(this.tagSetService.getById(markerSetId));
			this.selectedMarkerSet = result as TagSetResource;
			this.sortMarkerSetTags();
			if (cacheAsInitial) {
				this.initialMarkerSet = this.cloneMarkerSet(this.selectedMarkerSet);
			}
			this.cdr.markForCheck();
		} catch {
			this.selectedMarkerSet = null;
			if (cacheAsInitial) {
				this.initialMarkerSet = null;
			}
			this.cdr.markForCheck();
		}
	}

	private setMarkerControlValue (value: number | null, markDirty: boolean): void {
		const control = this.activityFormService.getControl('tag_set_id');
		control?.setValue(value);
		if (markDirty) {
			control?.markAsDirty();
			this.activityFormService.markFormAsDirty();
		} else {
			control?.markAsPristine();
		}
		if (this.activity) {
			this.activity.tag_set_id = value;
		}
	}

	private sortMarkerSetTags (): void {
		if (this.selectedMarkerSet?.tags?.length) {
			this.selectedMarkerSet.tags.sort((a, b) => (a.sort_index ?? 0) - (b.sort_index ?? 0));
		}
	}

	private cloneMarkerSet (markerSet: MarkerSet | null): MarkerSet | null {
		if (!markerSet) {
			return null;
		}
		return {
			...markerSet,
			tags: markerSet.tags?.map((tag) => ({
				...tag,
				category: tag.category ? { ...tag.category } : undefined
			})) ?? []
		};
	}

	private announceMarkerSetChange (hadMarkerSet: boolean): void {
		if (!hadMarkerSet && this.hasMarkerSet) {
			this.announceAccessibilityMessage('activity-editor-new_markers-added');
		} else if (hadMarkerSet && !this.hasMarkerSet) {
			this.announceAccessibilityMessage('activity-editor-new_markers-removed');
		}
	}

	private announceAccessibilityMessage (messageKey: string): void {
		const message = this.translate.instant(messageKey);
		if (this.activityEditAccessibilityAnnouncer != null) {
			this.activityEditAccessibilityAnnouncer.announce(message);
			return;
		}

		void this.liveAnnouncer.announce(message, 'assertive');
	}
}
